import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Rocket, Users, Calculator, Building } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const Projects = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const projects = [
    {
      name: "Skill4Future",
      url: "https://skill4future.de",
      description: "Plattform für die Zukunft der Weiterbildung und Kompetenzentwicklung",
      icon: Rocket,
      status: "Aktiv"
    },
    {
      name: "Gründermatch",
      url: "https://gruendermatch.de",
      description: "Netzwerk zur Vernetzung von Gründer:innen und Expert:innen",
      icon: Users,
      status: "Aktiv"
    },
    {
      name: "Lexo.Tax",
      url: "https://lexo.tax",
      description: "Innovative Lösung für Steuerwissen und -beratung",
      icon: Calculator,
      status: "Aktiv"
    },
    {
      name: "Walbee",
      url: "https://walbee.de",
      description: "Projekt im Bereich digitale Lösungen",
      icon: Building,
      status: "Aktiv"
    }
  ];

  return (
    <section id="projects" className="py-32 bg-gradient-subtle relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-[0.03]"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref}>
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold gradient-heading mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Meine Projekte
          </motion.h2>
          <motion.div 
            className="w-24 h-1 bg-gradient-to-r from-accent to-purple-accent mx-auto rounded-full mb-8"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          ></motion.div>
          <motion.p 
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Als Initiatorin und Mitgründerin verschiedener Projekte treibe ich 
            Innovation in der Beratungs- und Bildungsbranche voran.
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {projects.map((project, index) => (
            <motion.div
              key={project.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
              whileHover={{ y: -8 }}
            >
              <Card className="h-full border-2 border-border/50 hover:border-accent/30 transition-all duration-300 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-large)] bg-gradient-card relative overflow-hidden group">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-accent to-purple-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <CardHeader className="text-center">
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-br from-accent to-purple-accent rounded-xl flex items-center justify-center mx-auto mb-4 shadow-[var(--shadow-soft)]"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <project.icon className="w-8 h-8 text-white" strokeWidth={2} />
                  </motion.div>
                  <CardTitle className="text-lg text-foreground group-hover:text-accent transition-colors duration-300">{project.name}</CardTitle>
                  <div className="absolute top-4 right-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      project.status === 'Aktiv' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription className="text-sm text-muted-foreground mb-4 min-h-[40px]">
                    {project.description}
                  </CardDescription>
                  {project.url ? (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full group/btn border-2 hover:border-accent min-h-[40px]"
                      onClick={() => window.open(project.url, '_blank')}
                    >
                      Besuchen
                      <ExternalLink className="ml-2 w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="w-full min-h-[40px]" disabled>
                      Projekt abgeschlossen
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="bg-primary/5 border-2 border-primary/10 hover:border-accent/30 rounded-2xl p-8 max-w-2xl mx-auto shadow-[var(--shadow-soft)] transition-all duration-300">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Interesse an einer Zusammenarbeit?
            </h3>
            <p className="text-muted-foreground mb-6">
              Ob als Kooperationspartnerin, Dozentin oder Beraterin – 
              ich freue mich auf spannende Projekte und neue Herausforderungen.
            </p>
            <Button variant="cta" className="min-h-[44px]" asChild>
              <a href="#contact">Projekt besprechen</a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
